import { useState } from 'react';
import { PencilIcon, SearchIcon, XIcon } from 'lucide-react';
import { mag, type Bewijs, type Gevolgen, type OnderwerpRij } from '@vip/shared';

import { PeriodeKiezer } from '@/components/PeriodeKiezer';
import { Alert, AlertTitle } from '@/components/ui/alert';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Empty, EmptyHeader, EmptyTitle } from '@/components/ui/empty';
import { Field, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
} from '@/components/ui/input-group';
import { Item, ItemContent, ItemGroup, ItemTitle } from '@/components/ui/item';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { Spinner } from '@/components/ui/spinner';
import { useIsMobile } from '@/hooks/use-mobile';
import { ApiError } from '../../lib/apiClient';
import { ALLES, periodeVoorSoort, type PeriodeKeuze } from '../../lib/periode';
import { useAuth } from '../auth/useAuth';
import { filterTaxonomie, sorteerOpDrukte, telDeelonderwerpen } from './filter';
import {
  useBewijs,
  useHernoemOnderwerp,
  useTaxonomie,
  useWijzigDeelonderwerp,
} from './hulpvragen.api';

/** Dezelfde randen als op het begeleidingsscherm: gestippeld waar het kan,
 *  vol waar je nu boven hangt. Eén gebaar hoort er overal hetzelfde uit te zien. */
const DROP_KAN = 'outline-primary/40 outline-1 outline-dashed outline-offset-2';
const DROP_RAAK = 'bg-primary/5 outline-primary outline-2 outline-offset-2';

const SOORTEN = ['maand', 'jaar', 'altijd'] as const;

/**
 * Hulpvragen: de drie lagen waarin de app hulpvragen ordent, en de plek waar de
 * Admin ze bijstelt.
 *
 * Dit heette Onderwerpen en toonde alleen laag 1 en 2. Met het bewijs erbij
 * dekt het alle drie de lagen uit het besluit "Drie lagen, en de moeilijkheid
 * staat maar op één plek", en de onderste laag is de hulpvraag. Vandaar de naam.
 *
 * De lijst groeit doordat buddy's hulpvragen vaststellen, niet doordat iemand
 * hem invult. Wat hier gebeurt is dus corrigeren: een naam die te lang is, of
 * een deelonderwerp dat onder het verkeerde hoofdonderwerp hangt.
 *
 * Kijken mag iedereen met een inlog, het bewijs erbij. Corrigeren blijft bij
 * `vocabulary:manage`: hernoemen en verhangen verandert wat het dashboard
 * optelt. Zonder dat recht staat er geen potlood en valt er niets te slepen.
 *
 * ### Waarom één paneel
 *
 * Klikken op een deelonderwerp opent één paneel met zijn naam, zijn plek én het
 * bewijs. Die drie horen bij elkaar omdat je het bewijs nodig hebt om over de
 * andere twee te kunnen oordelen: of "Power BI" de juiste naam is en of het
 * onder Dashboarding hoort, lees je af aan de zinnen eronder. Stonden ze uit
 * elkaar, dan moest de Admin heen en weer.
 *
 * ### De periode
 *
 * De lijst gaat over één periode, standaard het lopende jaar, en "Altijd" telt
 * alles bij elkaar. Met een periode staat er alleen in wat er toen is
 * vastgesteld: een filter die alles laat staan en er nullen achter zet is geen
 * filter. Bijstellen doe je dus op Altijd, want daar staat de hele indeling.
 *
 * Verplaatsen gaat met een keuzelijst en niet met slepen. Het begeleidingsscherm
 * sleept wel, maar daar sleep je naar een kaart die in beeld staat; hier zijn
 * het zestien hoofdonderwerpen en badges die over regels heen lopen, en op een
 * telefoon is slepen sowieso geen doen.
 */
export function HulpvragenPagina() {
  const [soort, setSoort] = useState<PeriodeKeuze>('jaar');
  // Leeg laat de server kiezen: hij weet welke jaren er in de database zitten.
  const [periode, setPeriode] = useState<string>();
  const { data, isLoading } = useTaxonomie(periode);
  const { user } = useAuth();
  const magBijstellen = mag(user?.rechten, 'vocabulary:manage');
  const [zoek, setZoek] = useState('');
  const [open, setOpen] = useState<string | null>(null);
  const [hernoem, setHernoem] = useState<OnderwerpRij | null>(null);
  const [sleept, setSleept] = useState<{ id: string; vanaf: string } | null>(null);
  const [boven, setBoven] = useState<string | null>(null);
  const wijzig = useDeelWijziging(() => setOpen(null));

  if (isLoading) return <Skeleton className="h-96 w-full" />;

  const onderwerpen = data?.onderwerpen ?? [];
  // De eigen keuze wint van het antwoord van de server: dat loopt een tel achter
  // bij het wisselen, en dan zou de keuzelijst een maand tonen terwijl de
  // schakelaar al op jaar staat.
  const gekozen = periode && periode !== ALLES ? periode : (data?.periode?.periode ?? '');
  // En om dezelfde reden rekent de wissel met `gekozen` en niet met het
  // antwoord: wie snel doorklikt kwam anders in de vorige periode uit.
  const wisselSoort = (nieuw: PeriodeKeuze) => {
    setSoort(nieuw);
    setPeriode(periodeVoorSoort(nieuw, gekozen || undefined, data?.beschikbaar));
  };
  // Zonder gesprekken valt er nog niets vast te stellen, in geen enkele periode.
  const nogGeenGesprekken = data?.beschikbaar.maanden.length === 0;

  const zoekt = zoek.trim().length > 0;
  const gevonden = filterTaxonomie(onderwerpen, zoek);
  // Op aantal, want de vraag hier is wat het meest speelt. Binnen een blok ging
  // dat al zo; nu volgt de bovenste laag dezelfde regel. Dit stond op naam
  // "omdat je hier iets opzoekt", en dat opzoeken doet het zoekveld hierboven.
  const gesorteerd = sorteerOpDrukte(gevonden);

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>Hulpvragen</CardTitle>
          <CardDescription className="tabular-nums">
            {/* Tijdens het zoeken telt wat er in beeld staat, want dat is de vraag
                die je net hebt gesteld. */}
            {gevonden.length} {gevonden.length === 1 ? 'hoofdonderwerp' : 'hoofdonderwerpen'} ·{' '}
            {telDeelonderwerpen(gevonden)} deelonderwerpen
            {zoekt && ` van ${telDeelonderwerpen(onderwerpen)}`}
          </CardDescription>
          <CardAction className="flex flex-wrap items-center justify-end gap-2">
            {data && (
              <PeriodeKiezer
                beschikbaar={data.beschikbaar}
                onSoort={wisselSoort}
                onWaarde={setPeriode}
                soort={soort}
                soorten={SOORTEN}
                waarde={gekozen}
              />
            )}
            <InputGroup className="w-full sm:w-72">
              <InputGroupAddon>
                <SearchIcon />
              </InputGroupAddon>
              <InputGroupInput
                aria-label="Zoeken in de hulpvragen"
                onChange={(e) => setZoek(e.target.value)}
                placeholder="Zoek een onderwerp of thema"
                value={zoek}
              />
              {zoekt && (
                <InputGroupAddon align="inline-end">
                  <InputGroupButton aria-label="Zoekterm wissen" onClick={() => setZoek('')}>
                    <XIcon />
                  </InputGroupButton>
                </InputGroupAddon>
              )}
            </InputGroup>
          </CardAction>
        </CardHeader>
        <CardContent>
          {onderwerpen.length === 0 ? (
            <Empty>
              <EmptyHeader>
                <EmptyTitle>
                  {data?.periode && !nogGeenGesprekken
                    ? `Niets vastgesteld in ${data.periode.label}.`
                    : 'Nog geen hulpvragen. Ze ontstaan zodra een buddy een gesprek vaststelt.'}
                </EmptyTitle>
              </EmptyHeader>
            </Empty>
          ) : gesorteerd.length === 0 ? (
            <Empty>
              <EmptyHeader>
                <EmptyTitle>Niets gevonden voor “{zoek.trim()}”.</EmptyTitle>
              </EmptyHeader>
            </Empty>
          ) : (
            <ItemGroup>
              {gesorteerd.map(({ onderwerp: o, totaal }) => (
                <OnderwerpBlok
                  boven={boven === o.id}
                  key={o.id}
                  kanHier={magBijstellen && sleept !== null && sleept.vanaf !== o.id}
                  magBijstellen={magBijstellen}
                  onBewerk={() => setHernoem(o)}
                  onBoven={(aan) => setBoven(aan ? o.id : null)}
                  onKies={setOpen}
                  onLos={() => {
                    setSleept(null);
                    setBoven(null);
                  }}
                  onNeer={(deelId) => void wijzig.doe(deelId, { onderwerpId: o.id })}
                  onPak={(deelId) => setSleept({ id: deelId, vanaf: o.id })}
                  onderwerp={o}
                  totaal={totaal}
                />
              ))}
            </ItemGroup>
          )}
        </CardContent>
      </Card>

      <OnderwerpNaamDialog onderwerp={hernoem} onSluit={() => setHernoem(null)} />

      <BewijsPaneel
        deelonderwerpId={open}
        magBijstellen={magBijstellen}
        onSluit={() => setOpen(null)}
        periode={gekozen || undefined}
        wijzig={wijzig}
      />

      {wijzig.venster}
    </>
  );
}

/**
 * Een deelonderwerp wijzigen, met de vraag vooraf.
 *
 * Eén plek voor twee ingangen: slepen in de lijst en hernoemen in het paneel.
 * Allebei kunnen ze op een 409 uitkomen, en dan hoort er één venster te
 * verschijnen en niet twee. Vandaar dat de staat hier zit en niet in het paneel.
 *
 * De 409 is geen fout maar een vraag: de server stuurt in `details` mee wát er
 * verschuift, zodat het venster het kan noemen zonder het apart op te halen.
 */
function useDeelWijziging(onWeg: () => void) {
  const wijzig = useWijzigDeelonderwerp();
  const [bevestiging, setBevestiging] = useState<Bevestiging | null>(null);
  const [fout, setFout] = useState('');

  async function doe(
    id: string,
    invoer: { naam?: string; onderwerpId?: string },
    bevestigd = false,
  ): Promise<boolean> {
    setFout('');
    try {
      await wijzig.mutateAsync({ id, ...invoer, bevestigd });
      return true;
    } catch (err) {
      const melding = err instanceof ApiError ? err.message : String(err);
      if (err instanceof ApiError && err.status === 409 && !bevestigd) {
        setBevestiging({ id, melding, gevolgen: err.details as Gevolgen, invoer });
      } else setFout(melding);
      return false;
    }
  }

  const venster = (
    <BevestigDialog
      bevestiging={bevestiging}
      onDoor={() => {
        const staat = bevestiging;
        setBevestiging(null);
        if (!staat) return;
        void doe(staat.id, staat.invoer, true).then((gelukt) => {
          // Samenvoegen laat dit deelonderwerp verdwijnen; een paneel dat
          // ernaar kijkt zou daarna naar een rij kijken die er niet meer is.
          if (gelukt && staat.gevolgen?.samenvoegenMet) onWeg();
        });
      }}
      onSluit={() => setBevestiging(null)}
    />
  );

  return { bezig: wijzig.isPending, doe, fout, venster, wisFout: () => setFout('') };
}

type DeelWijziging = ReturnType<typeof useDeelWijziging>;

/**
 * Eén hoofdonderwerp met zijn deelonderwerpen als badges.
 *
 * Die vorm stond er al en werkt: je overziet in één blik wat eronder hangt, en
 * dat lukt met een rij pillen beter dan met een lijst die je regel voor regel
 * afgaat. Wat erbij is gekomen is het potlood en de klik.
 */
function OnderwerpBlok({
  boven,
  kanHier,
  magBijstellen,
  onBewerk,
  onBoven,
  onKies,
  onLos,
  onNeer,
  onPak,
  onderwerp,
  totaal,
}: {
  boven: boolean;
  kanHier: boolean;
  magBijstellen: boolean;
  onBewerk: () => void;
  onBoven: (aan: boolean) => void;
  onKies: (id: string) => void;
  onLos: () => void;
  onNeer: (deelonderwerpId: string) => void;
  onPak: (deelonderwerpId: string) => void;
  onderwerp: OnderwerpRij;
  totaal: number;
}) {
  // Binnen een hoofdonderwerp op aantal, want daar is de vraag niet "waar staat
  // het" maar "wat komt het meest terug".
  const deelonderwerpen = [...onderwerp.deelonderwerpen].sort(
    (a, b) => b.aantal - a.aantal || a.naam.localeCompare(b.naam, 'nl'),
  );

  const rand = !kanHier ? '' : boven ? DROP_RAAK : DROP_KAN;

  return (
    <Item
      className={`transition-colors ${rand}`}
      // `dragleave` vuurt ook bij het oversteken naar een kind; zonder deze
      // controle knippert de rand bij elke badge waar je overheen gaat.
      onDragLeave={(e) => {
        if (e.currentTarget.contains(e.relatedTarget as Node | null)) return;
        onBoven(false);
      }}
      onDragOver={(e) => {
        if (!kanHier) return;
        e.preventDefault();
        onBoven(true);
      }}
      onDrop={(e) => {
        e.preventDefault();
        const id = e.dataTransfer.getData('text/plain');
        onLos();
        if (kanHier && id) onNeer(id);
      }}
      variant="muted"
    >
      <ItemContent className="gap-2">
        <ItemTitle className="text-base">
          {onderwerp.naam}
          {totaal > 0 && (
            <span className="text-sm font-normal text-muted-foreground tabular-nums">{totaal}</span>
          )}
          {magBijstellen && (
            <Button
              aria-label={`${onderwerp.naam} hernoemen`}
              className="text-muted-foreground"
              onClick={onBewerk}
              size="icon-sm"
              variant="ghost"
            >
              <PencilIcon />
            </Button>
          )}
        </ItemTitle>
        <div className="flex flex-wrap gap-2">
          {deelonderwerpen.map((d) => (
            // `Badge` is gemaakt voor één woord: hij staat op `shrink-0`,
            // `whitespace-nowrap` en een vaste hoogte. Een deelonderwerp is een
            // paar woorden en kan uitschieten, en dan duwt zo'n pil het scherm
            // open in plaats van terug te vallen op een volgende regel.
            <Badge
              asChild
              className="h-auto max-w-full shrink whitespace-normal text-left"
              key={d.id}
              size="lg"
              variant="outline"
            >
              <button
                className={magBijstellen ? 'cursor-grab active:cursor-grabbing' : ''}
                draggable={magBijstellen}
                onClick={() => onKies(d.id)}
                onDragEnd={onLos}
                onDragStart={(e) => {
                  e.dataTransfer.setData('text/plain', d.id);
                  e.dataTransfer.effectAllowed = 'move';
                  onPak(d.id);
                }}
                type="button"
              >
                {d.naam}
                {d.aantal > 0 && (
                  <span className="text-muted-foreground tabular-nums">{d.aantal}</span>
                )}
              </button>
            </Badge>
          ))}
        </div>
      </ItemContent>
    </Item>
  );
}

/**
 * Het paneel: naam, plek en bewijs bij elkaar.
 *
 * Op een telefoon komt het van onderen op in plaats van van rechts. Een paneel
 * van driekwart schermbreedte naast een lijst werkt daar niet, en een blad dat
 * opkomt is het gebaar dat een telefoon kent.
 */
function BewijsPaneel({
  deelonderwerpId,
  magBijstellen,
  onSluit,
  periode,
  wijzig,
}: {
  deelonderwerpId: string | null;
  magBijstellen: boolean;
  onSluit: () => void;
  /** De periode van de lijst, zoals hij nu geteld is — dus ook het jaar dat de
   *  server koos toen het scherm openging. Zonder dat telt het paneel over
   *  alles en de badge over het jaar. Leeg is alles bij elkaar. */
  periode: string | undefined;
  wijzig: DeelWijziging;
}) {
  const telefoon = useIsMobile();
  const { data, isLoading } = useBewijs(deelonderwerpId, periode);

  return (
    <Sheet onOpenChange={(o) => !o && onSluit()} open={deelonderwerpId !== null}>
      <SheetContent
        className="gap-0 overflow-y-auto sm:max-w-md"
        side={telefoon ? 'bottom' : 'right'}
      >
        {isLoading || !data ? (
          <div className="p-6">
            <Skeleton className="h-64 w-full" />
          </div>
        ) : (
          <PaneelInhoud bewijs={data} magBijstellen={magBijstellen} wijzig={wijzig} />
        )}
      </SheetContent>
    </Sheet>
  );
}

function PaneelInhoud({
  bewijs,
  magBijstellen,
  wijzig,
}: {
  bewijs: Bewijs;
  magBijstellen: boolean;
  wijzig: DeelWijziging;
}) {
  const [naamOpen, setNaamOpen] = useState(false);

  async function hernoem(naam: string) {
    const gelukt = await wijzig.doe(bewijs.deelonderwerpId, { naam });
    if (gelukt) setNaamOpen(false);
  }

  return (
    <>
      <SheetHeader className="gap-1">
        <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          Deelonderwerp
        </span>
        <SheetTitle className="flex items-center gap-2 text-xl">
          {bewijs.naam}
          {magBijstellen && (
            <Button
              aria-label="Naam wijzigen"
              className="text-muted-foreground"
              onClick={() => setNaamOpen(true)}
              size="icon-sm"
              variant="ghost"
            >
              <PencilIcon />
            </Button>
          )}
        </SheetTitle>
        {/* Wáár het hangt staat er wel, maar als tekst: verhangen doe je door
            het in de lijst naar een ander blok te slepen. Een keuzelijst hier
            zou een tweede weg zijn voor dezelfde handeling. */}
        <p className="text-muted-foreground">Hangt onder {bewijs.onderwerpNaam}</p>
      </SheetHeader>

      <div className="flex flex-col gap-3 border-t px-4 py-5">
        <div className="flex items-baseline justify-between gap-2">
          <span className="font-heading text-base font-medium">Bewijs</span>
          <span className="text-sm text-muted-foreground tabular-nums">
            {bewijs.vragen.length} {bewijs.vragen.length === 1 ? 'hulpvraag' : 'hulpvragen'} uit{' '}
            {bewijs.gesprekken} {bewijs.gesprekken === 1 ? 'gesprek' : 'gesprekken'}
          </span>
        </div>

        {bewijs.vragen.length === 0 ? (
          <p className="text-muted-foreground">Hier hangt nog geen vastgestelde hulpvraag aan.</p>
        ) : (
          <div className="flex flex-col gap-2">
            {/* De zin zoals hij in het gesprek is gezegd, zonder wie en zonder
                welk gesprek. Dat is de grens uit plan §2.5, zo smal mogelijk
                gehouden: de woorden zijn nodig om over de naam te kunnen
                oordelen, de mensen niet. */}
            {bewijs.vragen.map((vraag, i) => (
              <p
                className="rounded-lg bg-muted/50 px-3.5 py-3 leading-relaxed text-pretty"
                key={`${i}-${vraag.slice(0, 24)}`}
              >
                {vraag}
              </p>
            ))}
          </div>
        )}

        {wijzig.fout && (
          <Alert variant="destructive">
            <AlertTitle>{wijzig.fout}</AlertTitle>
          </Alert>
        )}
      </div>

      <NaamDialog
        bezig={wijzig.bezig}
        fout={wijzig.fout}
        naam={bewijs.naam}
        onBewaar={(naam) => void hernoem(naam)}
        onSluit={() => {
          setNaamOpen(false);
          wijzig.wisFout();
        }}
        open={naamOpen}
        titel="Deelonderwerp"
      />
    </>
  );
}

/** Het hoofdonderwerp hernoemen. Eigen venster en niet het paneel, want een
 *  hoofdonderwerp heeft geen bewijs van zichzelf: dat hangt aan zijn
 *  deelonderwerpen. */
function OnderwerpNaamDialog({
  onSluit,
  onderwerp,
}: {
  onSluit: () => void;
  onderwerp: OnderwerpRij | null;
}) {
  const hernoem = useHernoemOnderwerp();
  const [fout, setFout] = useState('');
  const [bevestiging, setBevestiging] = useState<Bevestiging | null>(null);

  async function doe(naam: string, bevestigd = false) {
    if (!onderwerp) return;
    setFout('');
    try {
      await hernoem.mutateAsync({ id: onderwerp.id, naam, bevestigd });
      onSluit();
    } catch (err) {
      const melding = err instanceof ApiError ? err.message : String(err);
      if (err instanceof ApiError && err.status === 409 && !bevestigd) {
        setBevestiging({
          id: onderwerp.id,
          melding,
          gevolgen: err.details as Gevolgen,
          invoer: { naam },
        });
      } else setFout(melding);
    }
  }

  return (
    <>
      <NaamDialog
        bezig={hernoem.isPending}
        fout={fout}
        naam={onderwerp?.naam ?? ''}
        onBewaar={(naam) => void doe(naam)}
        onSluit={() => {
          setFout('');
          onSluit();
        }}
        open={onderwerp !== null}
        titel="Hoofdonderwerp"
      />

      <BevestigDialog
        bevestiging={bevestiging}
        onDoor={() => {
          const staat = bevestiging;
          setBevestiging(null);
          if (staat?.invoer.naam) void doe(staat.invoer.naam, true);
        }}
        onSluit={() => setBevestiging(null)}
      />
    </>
  );
}

/** Eén invoerveld, dezelfde vorm als het wijzigen per veld op de
 *  personenpagina. */
function NaamDialog({
  bezig,
  fout,
  naam,
  onBewaar,
  onSluit,
  open,
  titel,
}: {
  bezig: boolean;
  fout: string;
  naam: string;
  onBewaar: (naam: string) => void;
  onSluit: () => void;
  open: boolean;
  titel: string;
}) {
  const [waarde, setWaarde] = useState(naam);
  const [voor, setVoor] = useState(naam);
  // Bij het openen op de huidige naam beginnen, zonder effect: `open` en `naam`
  // veranderen samen, en dan is dit een gewone afgeleide van de invoer.
  if (naam !== voor) {
    setVoor(naam);
    setWaarde(naam);
  }

  const kan = waarde.trim().length >= 2 && waarde.trim() !== naam;

  return (
    <Dialog onOpenChange={(o) => !o && onSluit()} open={open}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{titel}</DialogTitle>
        </DialogHeader>

        <Field>
          <FieldLabel htmlFor="taxonomie-naam">Naam</FieldLabel>
          <Input
            autoComplete="off"
            autoFocus
            id="taxonomie-naam"
            onChange={(e) => setWaarde(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && kan && onBewaar(waarde.trim())}
            value={waarde}
          />
        </Field>

        {fout && (
          <Alert variant="destructive">
            <AlertTitle>{fout}</AlertTitle>
          </Alert>
        )}

        <DialogFooter>
          <Button disabled={!kan || bezig} onClick={() => onBewaar(waarde.trim())}>
            {bezig && <Spinner data-icon="inline-start" />}
            Opslaan
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface Bevestiging {
  id: string;
  melding: string;
  gevolgen: Gevolgen | undefined;
  invoer: { naam?: string; onderwerpId?: string };
}

/**
 * Wat er gaat gebeuren, voordat het gebeurt.
 *
 * Twee gevallen, één venster: samenvoegen laat een naam verdwijnen, verplaatsen
 * verschuift de telling van eerdere periodes. Allebei zijn ze bedoeld, en
 * allebei zie je ze pas achteraf als niemand het zegt.
 */
function BevestigDialog({
  bevestiging,
  onDoor,
  onSluit,
}: {
  bevestiging: Bevestiging | null;
  onDoor: () => void;
  onSluit: () => void;
}) {
  const gevolgen = bevestiging?.gevolgen;
  const samen = gevolgen?.samenvoegenMet ?? null;

  return (
    <AlertDialog onOpenChange={(o) => !o && onSluit()} open={bevestiging !== null}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {samen ? `Samenvoegen met ${samen.naam}?` : 'Verplaatsen?'}
          </AlertDialogTitle>
          <AlertDialogDescription>{bevestiging?.melding}</AlertDialogDescription>
        </AlertDialogHeader>

        {gevolgen && gevolgen.hulpvragen > 0 && (
          <div className="rounded-lg bg-muted/60 px-3.5 py-3 text-sm">
            <span className="tabular-nums">
              {gevolgen.hulpvragen} {gevolgen.hulpvragen === 1 ? 'hulpvraag' : 'hulpvragen'} uit{' '}
              {gevolgen.gesprekken} {gevolgen.gesprekken === 1 ? 'gesprek' : 'gesprekken'}
            </span>{' '}
            gaat mee. Het dashboard telt die daarna op de nieuwe plek, ook over eerdere periodes.
          </div>
        )}

        <AlertDialogFooter>
          <AlertDialogCancel>Annuleren</AlertDialogCancel>
          <AlertDialogAction onClick={onDoor}>
            {samen ? 'Ja, samenvoegen' : 'Verplaatsen'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
